import { ReactNode } from 'react';
import SharedSvg from './shared-svg';
import { SxProps, Theme } from '@mui/material/styles';
import DialogTitle from '@mui/material/DialogTitle';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface Button {
  title: string;
  hasTooltip?: boolean;
  tooltipTitle?: string;
  action: () => void;
  loading: boolean;
  disabled: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'warning' | 'error';
  shown?: boolean;
  alignLeft?: boolean;
  sx?: SxProps<Theme>;
}

interface GeneralDialogProps {
  open: boolean;
  handleClose?: () => void;
  title?: string;
  dialogContent: ReactNode;
  primaryButton?: Button;
  secondaryButton?: Button;
  tertiaryButton?: Button;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showErrorColorOnTitleUnderline?: boolean;
  disableRestoreFocus?: boolean;
  errorMessage?: string;
  actionsRightContent?: ReactNode;
}

export default function GeneralDialog({
  open,
  handleClose,
  title,
  primaryButton,
  secondaryButton,
  tertiaryButton,
  dialogContent,
  maxWidth,
  showErrorColorOnTitleUnderline,
  disableRestoreFocus,
  errorMessage,
  actionsRightContent,
}: GeneralDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={handleClose ? () => handleClose() : undefined}
      fullWidth
      maxWidth={maxWidth ? maxWidth : 'sm'}
      disableRestoreFocus={disableRestoreFocus ? disableRestoreFocus : false}
    >
      {title && (
        <>
          <DialogTitle
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <SharedSvg
                variant={showErrorColorOnTitleUnderline ? 'red' : 'default'}
                sx={{ mr: 1 }}
              />
              <Typography variant="h6" component="span" sx={{ pt: 0.2 }}>
                {title}
              </Typography>
            </Stack>
          </DialogTitle>
          <Divider />
        </>
      )}

      {handleClose && (
        <IconButton
          sx={{ position: 'absolute', top: 0, right: 0 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
      )}
      <DialogContent sx={{ pt: 1, pb: 2 }}>{dialogContent}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {errorMessage && !actionsRightContent && (
          <Typography
            color="error"
            variant="caption"
            sx={{ mr: 'auto', ml: '14px' }}
          >
            {errorMessage}
          </Typography>
        )}
        {errorMessage && actionsRightContent && (
          <Typography color="error" variant="caption" sx={{ mr: 2 }}>
            {errorMessage}
          </Typography>
        )}
        {actionsRightContent && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, mr: 1 }}>
            {actionsRightContent}
          </Box>
        )}
        {secondaryButton && (
          <Button
            loading={secondaryButton.loading}
            variant={
              secondaryButton.variant ? secondaryButton.variant : 'outlined'
            }
            onClick={secondaryButton.action}
            disabled={secondaryButton.disabled}
            color={secondaryButton.color ? secondaryButton.color : 'primary'}
            sx={{
              display: secondaryButton.shown ? 'block' : 'none',
              ...(secondaryButton.alignLeft && { mr: 'auto' }),
              ...secondaryButton.sx,
            }}
          >
            {secondaryButton.title}
          </Button>
        )}
        {tertiaryButton && (
          <Button
            loading={tertiaryButton.loading}
            variant={
              tertiaryButton.variant ? tertiaryButton.variant : 'outlined'
            }
            onClick={tertiaryButton.action}
            disabled={tertiaryButton.disabled}
            color={tertiaryButton.color ? tertiaryButton.color : 'primary'}
            sx={{
              display: tertiaryButton.shown ? 'block' : 'none',
              ...(tertiaryButton.alignLeft && { mr: 'auto' }),
              ...tertiaryButton.sx,
            }}
          >
            {tertiaryButton.title}
          </Button>
        )}
        {primaryButton && primaryButton.hasTooltip ? (
          <Tooltip title={primaryButton.tooltipTitle} arrow placement="top">
            <span>
              <Button
                loading={primaryButton.loading}
                variant={
                  primaryButton.variant ? primaryButton.variant : 'contained'
                }
                onClick={primaryButton.action}
                disabled={primaryButton.disabled}
                color={primaryButton.color ? primaryButton.color : 'primary'}
                sx={primaryButton.sx}
              >
                {primaryButton.title}
              </Button>
            </span>
          </Tooltip>
        ) : primaryButton ? (
          <Button
            loading={primaryButton.loading}
            variant={
              primaryButton.variant ? primaryButton.variant : 'contained'
            }
            onClick={primaryButton.action}
            disabled={primaryButton.disabled}
            color={primaryButton.color ? primaryButton.color : 'primary'}
            sx={primaryButton.sx}
          >
            {primaryButton.title}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
